import { useEffect, useMemo, useState } from "react"
import { Plus, Pencil, Trash2 } from "lucide-react"
import { Button } from "../../components/ui/Button"
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card"
import { Input } from "../../components/ui/Input"
import { Select } from "../../components/ui/Select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table"
import { TableSkeleton } from "../../components/ui/Skeleton"
import { EmptyState } from "../../components/ui/EmptyState"
import { fr } from "../../locales/fr"
import {
  getExpenseTypes,
  createExpenseType,
  updateExpenseType,
  deleteExpenseType,
} from "../../services/expenseTypeService"
import { getExpenses, createExpense, updateExpense, deleteExpense } from "../../services/expenseService"

const emptyTypeForm = { name: "", code: "", color: "", sort_order: "100" }
const emptyExpenseForm = { title: "", amount: "", expense_type_id: "", created_at: "" }

function formatMoney(n) {
  const x = Number(n)
  if (Number.isNaN(x)) return "—"
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "MAD" }).format(x)
}

function toDatetimeLocal(iso) {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export default function Expenses() {
  const [types, setTypes] = useState([])
  const [expenses, setExpenses] = useState([])
  const [loadingTypes, setLoadingTypes] = useState(true)
  const [loadingExpenses, setLoadingExpenses] = useState(true)
  const [filterTypeId, setFilterTypeId] = useState("")
  const [showTypeForm, setShowTypeForm] = useState(false)
  const [editingTypeId, setEditingTypeId] = useState(null)
  const [typeForm, setTypeForm] = useState(emptyTypeForm)
  const [showExpenseForm, setShowExpenseForm] = useState(false)
  const [editingExpenseId, setEditingExpenseId] = useState(null)
  const [expenseForm, setExpenseForm] = useState(emptyExpenseForm)
  const [saving, setSaving] = useState(false)

  const loadTypes = async () => {
    setLoadingTypes(true)
    try {
      const data = await getExpenseTypes()
      setTypes(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
      setTypes([])
    }
    setLoadingTypes(false)
  }

  const loadExpenses = async () => {
    setLoadingExpenses(true)
    try {
      const params = {}
      if (filterTypeId) params.expense_type_id = filterTypeId
      const data = await getExpenses(params)
      setExpenses(Array.isArray(data) ? data : [])
    } catch (e) {
      console.error(e)
      setExpenses([])
    }
    setLoadingExpenses(false)
  }

  useEffect(() => {
    loadTypes()
  }, [])

  useEffect(() => {
    loadExpenses()
  }, [filterTypeId])

  const totalShown = useMemo(() => expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0), [expenses])

  const submitType = async (e) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      name: typeForm.name.trim(),
      code: typeForm.code.trim() || undefined,
      color: typeForm.color.trim() || null,
      sort_order: parseInt(typeForm.sort_order, 10) || 0,
    }
    try {
      if (editingTypeId) {
        await updateExpenseType(editingTypeId, payload)
      } else {
        await createExpenseType(payload)
      }
      setShowTypeForm(false)
      setEditingTypeId(null)
      setTypeForm(emptyTypeForm)
      await loadTypes()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
    setSaving(false)
  }

  const removeType = async (id) => {
    if (!window.confirm(fr.expensesPage.confirmDeleteType)) return
    try {
      await deleteExpenseType(id)
      await loadTypes()
      await loadExpenses()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
  }

  const startEditType = (t) => {
    setEditingTypeId(t.id)
    setTypeForm({
      name: t.name || "",
      code: t.code || "",
      color: t.color || "",
      sort_order: String(t.sortOrder ?? 100),
    })
    setShowTypeForm(true)
  }

  const submitExpense = async (e) => {
    e.preventDefault()
    setSaving(true)
    const payload = {
      title: expenseForm.title.trim(),
      amount: parseFloat(expenseForm.amount),
      expense_type_id: parseInt(expenseForm.expense_type_id, 10),
    }
    if (expenseForm.created_at) {
      payload.created_at = new Date(expenseForm.created_at).toISOString()
    }
    try {
      if (editingExpenseId) {
        await updateExpense(editingExpenseId, payload)
      } else {
        await createExpense(payload)
      }
      setShowExpenseForm(false)
      setEditingExpenseId(null)
      setExpenseForm(emptyExpenseForm)
      await loadExpenses()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
    setSaving(false)
  }

  const removeExpense = async (id) => {
    if (!window.confirm(fr.expensesPage.confirmDeleteExpense)) return
    try {
      await deleteExpense(id)
      await loadExpenses()
    } catch (err) {
      console.error(err)
      alert(err.response?.data?.error || "Erreur")
    }
  }

  const startEditExpense = (row) => {
    setEditingExpenseId(row.id)
    setExpenseForm({
      title: row.title || "",
      amount: String(row.amount ?? ""),
      expense_type_id: String(row.expenseType?.id ?? ""),
      created_at: toDatetimeLocal(row.createdAt),
    })
    setShowExpenseForm(true)
  }

  const openNewExpense = () => {
    setEditingExpenseId(null)
    setExpenseForm({
      ...emptyExpenseForm,
      expense_type_id: types[0]?.id ? String(types[0].id) : "",
      created_at: toDatetimeLocal(new Date().toISOString()),
    })
    setShowExpenseForm(true)
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-foreground">{fr.expensesPage.title}</h1>
        <p className="text-muted-foreground">{fr.expensesPage.subtitle}</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{fr.expensesPage.typesTitle}</CardTitle>
            <p className="text-sm text-muted-foreground">{fr.expensesPage.typesSubtitle}</p>
          </div>
          <Button
            type="button"
            onClick={() => {
              setEditingTypeId(null)
              setTypeForm(emptyTypeForm)
              setShowTypeForm(true)
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {fr.expensesPage.addType}
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {showTypeForm && (
            <form onSubmit={submitType} className="grid gap-3 rounded-lg border border-border p-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">{fr.expensesPage.typeName}</label>
                <Input
                  value={typeForm.name}
                  onChange={(ev) => setTypeForm({ ...typeForm, name: ev.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.expensesPage.typeCode}</label>
                <Input
                  value={typeForm.code}
                  onChange={(ev) => setTypeForm({ ...typeForm, code: ev.target.value })}
                  placeholder="ex. loyer"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.expensesPage.sortOrder}</label>
                <Input
                  type="number"
                  value={typeForm.sort_order}
                  onChange={(ev) => setTypeForm({ ...typeForm, sort_order: ev.target.value })}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.expensesPage.typeColor}</label>
                <Input
                  type="color"
                  className="h-10 w-24 cursor-pointer p-1"
                  value={typeForm.color || "#64748b"}
                  onChange={(ev) => setTypeForm({ ...typeForm, color: ev.target.value })}
                />
              </div>
              <div className="flex items-end gap-2 md:col-span-2">
                <Button type="submit" disabled={saving}>
                  {fr.common.save}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowTypeForm(false)}>
                  {fr.common.cancel}
                </Button>
              </div>
            </form>
          )}
          {loadingTypes ? (
            <TableSkeleton rows={4} columns={4} />
          ) : types.length === 0 ? (
            <EmptyState title={fr.expensesPage.emptyTypes} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fr.expensesPage.type}</TableHead>
                  <TableHead>{fr.expensesPage.typeCode}</TableHead>
                  <TableHead>{fr.expensesPage.sortOrder}</TableHead>
                  <TableHead>{fr.common.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {types.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">
                      <span className="inline-flex items-center gap-2">
                        {t.color && (
                          <span
                            className="inline-block h-3 w-3 rounded-full border border-border"
                            style={{ backgroundColor: t.color }}
                          />
                        )}
                        {t.name}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{t.code}</TableCell>
                    <TableCell>{t.sortOrder}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => startEditType(t)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => removeType(t.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>{fr.expensesPage.listTitle}</CardTitle>
            <p className="text-sm text-muted-foreground">
              {fr.expensesPage.totalPeriod} : {formatMoney(totalShown)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={filterTypeId}
              onChange={(ev) => setFilterTypeId(ev.target.value)}
              className="min-w-[200px]"
            >
              <option value="">{fr.expensesPage.allTypes}</option>
              {types.map((t) => (
                <option key={t.id} value={String(t.id)}>
                  {t.name}
                </option>
              ))}
            </Select>
            <Button type="button" onClick={openNewExpense} disabled={types.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              {fr.expensesPage.addExpense}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {showExpenseForm && (
            <form onSubmit={submitExpense} className="grid gap-3 rounded-lg border border-border p-4 md:grid-cols-2">
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">{fr.expensesPage.expenseTitle}</label>
                <Input
                  value={expenseForm.title}
                  onChange={(ev) => setExpenseForm({ ...expenseForm, title: ev.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.expensesPage.amount}</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={expenseForm.amount}
                  onChange={(ev) => setExpenseForm({ ...expenseForm, amount: ev.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">{fr.expensesPage.type}</label>
                <Select
                  value={expenseForm.expense_type_id}
                  onChange={(ev) => setExpenseForm({ ...expenseForm, expense_type_id: ev.target.value })}
                  required
                >
                  <option value="">{fr.expensesPage.type}</option>
                  {types.map((t) => (
                    <option key={t.id} value={String(t.id)}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium">{fr.expensesPage.date}</label>
                <Input
                  type="datetime-local"
                  value={expenseForm.created_at}
                  onChange={(ev) => setExpenseForm({ ...expenseForm, created_at: ev.target.value })}
                />
              </div>
              <div className="flex gap-2 md:col-span-2">
                <Button type="submit" disabled={saving}>
                  {fr.common.save}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowExpenseForm(false)}>
                  {fr.common.cancel}
                </Button>
              </div>
            </form>
          )}
          {loadingExpenses ? (
            <TableSkeleton rows={5} columns={5} />
          ) : expenses.length === 0 ? (
            <EmptyState title={fr.expensesPage.emptyExpenses} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{fr.expensesPage.date}</TableHead>
                  <TableHead>{fr.expensesPage.expenseTitle}</TableHead>
                  <TableHead>{fr.expensesPage.type}</TableHead>
                  <TableHead className="text-right">{fr.expensesPage.amount}</TableHead>
                  <TableHead>{fr.common.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {row.createdAt
                        ? new Date(row.createdAt).toLocaleString("fr-FR", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })
                        : "—"}
                    </TableCell>
                    <TableCell className="font-medium">{row.title}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-2">
                        {row.expenseType?.color && (
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full border border-border"
                            style={{ backgroundColor: row.expenseType.color }}
                          />
                        )}
                        {row.expenseType?.name ?? "—"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatMoney(row.amount)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button type="button" variant="ghost" size="sm" onClick={() => startEditExpense(row)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="text-destructive"
                          onClick={() => removeExpense(row.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
